import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, Brain, Plus, Edit, Trash2, Save, X, Star, Upload } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_PROFILE = {
  slug: '',
  name: '',
  title: { es: '', en: '' },
  nickname: { es: '', en: '' },
  avatar: '',
  rating: 4.5,
  badges: [
    { icon: 'star', text: { es: '', en: '' } },
    { icon: 'star', text: { es: '', en: '' } },
  ],
  quote: { es: '', en: '' },
  bio: { es: '', en: '' },
  system_prompt: '',
};

export default function AdminAIProfiles() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, object = form view
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const t = (es, en) => language === 'es' ? es : en;

  const loadProfiles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/ai-evaluators/profiles`);
      const data = await res.json();
      setProfiles(data);
    } catch (e) {
      console.error('Error loading profiles:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const handleSave = async () => {
    if (!editing.slug || !editing.name) {
      toast({ title: t('Slug y nombre son requeridos', 'Slug and name are required'), variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const isNew = !editing._id;
      const url = isNew
        ? `${API_URL}/ai-evaluators/profiles`
        : `${API_URL}/ai-evaluators/profiles/${editing._id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error saving');
      }
      toast({ title: t('Perfil guardado', 'Profile saved') });
      setEditing(null);
      await loadProfiles();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Eliminar este perfil?', 'Delete this profile?'))) return;
    try {
      const res = await fetch(`${API_URL}/ai-evaluators/profiles/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast({ title: t('Perfil eliminado', 'Profile deleted') });
      await loadProfiles();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const updateField = (path, value) => {
    setEditing(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let target = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef0f4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  // FORM VIEW
  if (editing) {
    return (
      <div className="min-h-screen bg-[#eef0f4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6" data-testid="ai-profile-form">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {editing._id ? t('Editar Perfil IA', 'Edit AI Profile') : t('Nuevo Perfil IA', 'New AI Profile')}
          </h1>
          <Button variant="ghost" onClick={() => setEditing(null)} data-testid="cancel-btn">
            <X className="w-4 h-4 mr-1" /> {t('Cancelar', 'Cancel')}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic info */}
          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">{t('Informacion Basica', 'Basic Info')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t('Slug (ID unico)', 'Slug (unique ID)')}</Label>
                <Input value={editing.slug} onChange={e => updateField('slug', e.target.value)} placeholder="business_expert" data-testid="slug-input" disabled={!!editing._id} />
              </div>
              <div>
                <Label>{t('Nombre', 'Name')}</Label>
                <Input value={editing.name} onChange={e => updateField('name', e.target.value)} placeholder="Carolina Mendez" data-testid="name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('Titulo (ES)', 'Title (ES)')}</Label>
                  <Input value={editing.title?.es || ''} onChange={e => updateField('title.es', e.target.value)} />
                </div>
                <div>
                  <Label>{t('Titulo (EN)', 'Title (EN)')}</Label>
                  <Input value={editing.title?.en || ''} onChange={e => updateField('title.en', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('Apodo (ES)', 'Nickname (ES)')}</Label>
                  <Input value={editing.nickname?.es || ''} onChange={e => updateField('nickname.es', e.target.value)} />
                </div>
                <div>
                  <Label>{t('Apodo (EN)', 'Nickname (EN)')}</Label>
                  <Input value={editing.nickname?.en || ''} onChange={e => updateField('nickname.en', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>{t('Avatar', 'Avatar')}</Label>
                <div className="flex items-start gap-3 mt-1">
                  {editing.avatar && <img src={editing.avatar.startsWith('/') ? process.env.REACT_APP_BACKEND_URL + editing.avatar : editing.avatar} alt="preview" className="w-16 h-16 rounded-xl object-cover border-2 border-gray-100 shadow-sm flex-shrink-0" />}
                  <div className="flex-1 space-y-2">
                    <label
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                        uploadingAvatar ? 'border-gray-300 bg-gray-50 text-gray-400' : 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100'
                      }`}
                    >
                      {uploadingAvatar ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {t('Subiendo...', 'Uploading...')}</>
                      ) : (
                        <><Upload className="w-4 h-4" /> {t('Subir imagen', 'Upload image')}</>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingAvatar}
                        data-testid="avatar-upload-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploadingAvatar(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await fetch(`${API_URL}/uploads/avatar`, { method: 'POST', body: formData });
                            const data = await res.json();
                            if (data.success) {
                              updateField('avatar', data.url);
                            }
                          } catch (err) {
                            console.error('Avatar upload error:', err);
                          } finally {
                            setUploadingAvatar(false);
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                    <Input
                      value={editing.avatar}
                      onChange={e => updateField('avatar', e.target.value)}
                      placeholder="https://... o /api/uploads/avatars/..."
                      className="text-xs"
                      data-testid="avatar-input"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>Rating (1-5)</Label>
                <Input type="number" min="1" max="5" step="0.1" value={editing.rating} onChange={e => updateField('rating', parseFloat(e.target.value))} />
              </div>
            </CardContent>
          </Card>

          {/* Badges + Quote */}
          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">{t('Badges y Cita', 'Badges & Quote')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(editing.badges || []).map((badge, bi) => (
                <div key={bi} className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <Label className="text-xs text-gray-500">Badge {bi + 1}</Label>
                  <Input value={badge.icon} onChange={e => updateField(`badges.${bi}.icon`, e.target.value)} placeholder="icon name (dollar-sign, rocket, etc.)" className="text-sm" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={badge.text?.es || ''} onChange={e => updateField(`badges.${bi}.text.es`, e.target.value)} placeholder="Texto ES" className="text-sm" />
                    <Input value={badge.text?.en || ''} onChange={e => updateField(`badges.${bi}.text.en`, e.target.value)} placeholder="Text EN" className="text-sm" />
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => updateField('badges', [...(editing.badges || []), { icon: 'star', text: { es: '', en: '' } }])}>
                <Plus className="w-3 h-3 mr-1" /> {t('Agregar badge', 'Add badge')}
              </Button>

              <div className="pt-3 border-t border-gray-200">
                <Label>{t('Cita (ES)', 'Quote (ES)')}</Label>
                <Textarea value={editing.quote?.es || ''} onChange={e => updateField('quote.es', e.target.value)} rows={2} />
              </div>
              <div>
                <Label>{t('Cita (EN)', 'Quote (EN)')}</Label>
                <Textarea value={editing.quote?.en || ''} onChange={e => updateField('quote.en', e.target.value)} rows={2} />
              </div>
            </CardContent>
          </Card>

          {/* Bio / Profile Description - full width */}
          <Card className="border border-gray-200 lg:col-span-2">
            <CardHeader><CardTitle className="text-base">{t('Descripcion del Perfil', 'Profile Description')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-gray-500">{t('Esta descripcion aparece en la ventana "Ver perfil" de la ficha del evaluador.', 'This description appears in the "View profile" popup of the evaluator card.')}</p>
              <div>
                <Label>{t('Descripcion (ES)', 'Description (ES)')}</Label>
                <Textarea
                  value={editing.bio?.es || ''}
                  onChange={e => updateField('bio.es', e.target.value)}
                  rows={5}
                  data-testid="bio-es-input"
                  placeholder={t('Descripcion en prosa del evaluador IA...', 'Prose description of the AI evaluator...')}
                />
              </div>
              <div>
                <Label>{t('Descripcion (EN)', 'Description (EN)')}</Label>
                <Textarea
                  value={editing.bio?.en || ''}
                  onChange={e => updateField('bio.en', e.target.value)}
                  rows={5}
                  data-testid="bio-en-input"
                  placeholder={t('Prose description of the AI evaluator...', 'Prose description of the AI evaluator...')}
                />
              </div>
            </CardContent>
          </Card>

          {/* System Prompt - full width */}
          <Card className="border border-gray-200 lg:col-span-2">
            <CardHeader><CardTitle className="text-base">{t('System Prompt (Instrucciones IA)', 'System Prompt (AI Instructions)')}</CardTitle></CardHeader>
            <CardContent>
              <Textarea
                value={editing.system_prompt || ''}
                onChange={e => updateField('system_prompt', e.target.value)}
                rows={12}
                className="font-mono text-sm"
                data-testid="system-prompt-input"
                placeholder={t('Eres un evaluador experto en...', 'You are an expert evaluator in...')}
              />
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setEditing(null)}>{t('Cancelar', 'Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#6949b6' }} className="text-white" data-testid="save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {t('Guardar', 'Save')}
          </Button>
        </div>
      </div>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="min-h-screen bg-[#eef0f4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="space-y-6" data-testid="admin-ai-profiles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('Perfiles IA', 'AI Profiles')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('Gestiona los evaluadores de inteligencia artificial', 'Manage AI evaluator profiles')}</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY_PROFILE })} style={{ backgroundColor: '#6949b6' }} className="text-white" data-testid="new-profile-btn">
          <Plus className="w-4 h-4 mr-1" /> {t('Nuevo Perfil', 'New Profile')}
        </Button>
      </div>

      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Perfil', 'Profile')}</TableHead>
                  <TableHead>{t('Titulo', 'Title')}</TableHead>
                  <TableHead>{t('Apodo', 'Nickname')}</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Badges</TableHead>
                  <TableHead>{t('Acciones', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map(profile => {
                  const titleText = profile.title?.[language] || profile.title?.es || '';
                  const nicknameText = profile.nickname?.[language] || profile.nickname?.es || '';
                  return (
                    <TableRow key={profile._id || profile.slug} data-testid={`ai-row-${profile.slug}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img src={profile.avatar} alt={profile.name} className="w-10 h-10 rounded-full object-cover" />
                          <div>
                            <p className="font-semibold text-gray-900">{profile.name}</p>
                            <p className="text-xs text-gray-500">{profile.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-700">{titleText}</TableCell>
                      <TableCell className="text-sm text-gray-500 italic">&ldquo;{nicknameText}&rdquo;</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="text-sm">{profile.rating}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(profile.badges || []).map((b, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{b.text?.[language] || b.text?.es || '?'}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => {
                            // Fetch full profile with system_prompt
                            fetch(`${API_URL}/ai-evaluators/profiles/${profile.slug}`)
                              .then(r => r.json())
                              .then(full => setEditing({ ...full, bio: full.bio || { es: '', en: '' } }))
                              .catch(() => setEditing({ ...profile, bio: profile.bio || { es: '', en: '' } }));
                          }} data-testid={`edit-btn-${profile.slug}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50" onClick={() => handleDelete(profile._id)} data-testid={`delete-btn-${profile.slug}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
      </div>
    </div>
  );
}
